/** FASE 5B · Jun 2026 — Importador manual de CSV Bank of America (solo Admin). */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useI18n } from "@/context/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(v || 0);

const TYPES = ["gasto", "reembolso_gasto", "transferencia_no_contabilizar", "deposito_no_contabilizar"];
const CATEGORIES = ["materiales", "publicidad", "software_suscripciones", "servicios", "renta",
  "nomina", "impuestos", "transporte", "comisiones_bancarias", "sales_tax_remitido", "otros"];

const needsCategory = (t) => t === "gasto" || t === "reembolso_gasto";

export const BankImport = () => {
  const { t } = useI18n();
  const fileRef = useRef(null);
  const [filename, setFilename] = useState("");
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [customCats, setCustomCats] = useState([]);
  const [newCat, setNewCat] = useState(null); // { rowIndex } | null
  const [newCatName, setNewCatName] = useState("");
  const [catError, setCatError] = useState("");
  const [showManage, setShowManage] = useState(false);

  const loadCats = () =>
    axios.get(`${API}/api/finance/bank/categories`).then((r) => setCustomCats(r.data.custom)).catch(() => {});

  useEffect(() => { loadCats(); }, []);

  const activeCustom = customCats.filter((c) => c.active);

  const createCategory = async () => {
    setCatError("");
    try {
      const { data } = await axios.post(`${API}/api/finance/bank/categories`, { name: newCatName });
      await loadCats();
      if (newCat?.rowIndex != null) setRow(newCat.rowIndex, { category: data.name });
      setNewCat(null); setNewCatName("");
    } catch (err) {
      setCatError(err?.response?.data?.detail || t("fin_bi_error"));
    }
  };

  const toggleCat = async (c) => {
    await axios.patch(`${API}/api/finance/bank/categories/${c.id}`, { active: !c.active });
    loadCats();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(""); setResult(null); setPreview(null); setRows([]); setBusy(true);
    setFilename(f.name);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/api/finance/bank/preview`, fd);
      setPreview(data);
      setRows(data.rows.map((r) => ({ ...r, movement_type: r.suggested_type, category: r.suggested_category })));
    } catch (err) {
      setError(err?.response?.data?.detail || t("fin_bi_error"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setRow = (i, patch) => setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const selectable = rows.filter((r) => !r.duplicate);
  const gastos = selectable.filter((r) => r.movement_type === "gasto");
  const reembolsos = selectable.filter((r) => r.movement_type === "reembolso_gasto");
  const excluidos = selectable.filter((r) => !needsCategory(r.movement_type));
  const gastosTotal = gastos.reduce((s, r) => s + Math.abs(r.amount), 0);
  const reembolsosTotal = reembolsos.reduce((s, r) => s + r.amount, 0);

  const onConfirm = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        filename,
        rows: selectable.map((r) => ({
          date: r.date, description: r.description, amount: r.amount, ordinal: r.ordinal,
          movement_type: r.movement_type,
          category: needsCategory(r.movement_type) ? r.category : null,
        })),
      };
      const { data } = await axios.post(`${API}/api/finance/bank/commit`, payload);
      setResult(data);
      setPreview(null); setRows([]);
    } catch (err) {
      setError(err?.response?.data?.detail || t("fin_bi_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="fin-bank-import" className="space-y-4">
      <div className="label-overline">{t("fin_bi_title")}</div>

      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" id="bank-csv-input" data-testid="fin-bi-file-input" />
        <label htmlFor="bank-csv-input"
          className="cursor-pointer border border-black bg-black text-white px-4 py-2 text-sm font-mono-tech hover:bg-white hover:text-black transition-colors"
          data-testid="fin-bi-upload-btn">
          {busy ? t("fin_bi_loading") : t("fin_bi_upload")}
        </label>
        {filename && <span className="text-xs font-mono-tech text-[var(--text-secondary)]">{filename}</span>}
        <button onClick={() => setShowManage((v) => !v)} data-testid="fin-bi-manage-cats-btn"
          className="border border-[var(--border)] bg-white px-3 py-2 text-xs font-mono-tech hover:border-black transition-colors">
          {t("fin_bi_manage_cats")}
        </button>
      </div>

      {showManage && (
        <div data-testid="fin-bi-manage-panel" className="border border-[var(--border)] bg-white p-4 space-y-2 max-w-md">
          <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t("fin_bi_custom_cats")}</div>
          {customCats.length === 0 && <div className="text-xs italic text-[var(--text-secondary)]">—</div>}
          {customCats.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 text-sm">
              <span className={c.active ? "" : "line-through text-[var(--text-secondary)]"}>{c.name}</span>
              <button onClick={() => toggleCat(c)} data-testid={`fin-bi-cat-toggle-${c.id}`}
                className="border border-[var(--border)] px-2 py-0.5 text-[10px] font-mono-tech uppercase hover:border-black">
                {c.active ? t("fin_bi_deactivate") : t("fin_bi_activate")}
              </button>
            </div>
          ))}
          <button onClick={() => { setNewCat({ rowIndex: null }); setNewCatName(""); setCatError(""); }}
            data-testid="fin-bi-new-cat-btn"
            className="text-xs font-mono-tech underline">{t("fin_bi_new_cat")}</button>
        </div>
      )}

      {newCat && (
        <div data-testid="fin-bi-new-cat-form" className="border border-black bg-white p-4 space-y-2 max-w-md">
          <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t("fin_bi_new_cat_title")}</div>
          <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} maxLength={40} autoFocus
            data-testid="fin-bi-new-cat-input"
            className="border border-[var(--border)] px-2 py-1.5 text-sm w-full" placeholder={t("fin_bi_new_cat_title")} />
          {catError && <div data-testid="fin-bi-new-cat-error" className="text-xs text-[#C5221F]">{catError}</div>}
          <div className="flex gap-2">
            <button onClick={createCategory} data-testid="fin-bi-new-cat-save"
              className="border border-black bg-black text-white px-4 py-1.5 text-xs font-mono-tech hover:bg-white hover:text-black transition-colors">
              {t("fin_bi_create")}
            </button>
            <button onClick={() => setNewCat(null)} data-testid="fin-bi-new-cat-cancel"
              className="border border-[var(--border)] px-4 py-1.5 text-xs font-mono-tech">{t("fin_bi_cancel")}</button>
          </div>
        </div>
      )}

      {error && <div data-testid="fin-bi-error" className="border-l-4 border-[#C5221F] bg-[#FDECEA] px-4 py-2 text-sm">{error}</div>}

      {result && (
        <div data-testid="fin-bi-result" className="border-l-4 border-[#0F7B3E] bg-[#E9F6EE] px-4 py-3 text-sm font-mono-tech">
          {t("fin_bi_imported")}: <b>{result.imported}</b> · {t("fin_bi_skipped")}: <b>{result.skipped_duplicates}</b>
          {result.rejected?.length > 0 && <> · {t("fin_bi_rejected")}: <b>{result.rejected.length}</b></>}
        </div>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              ["fin_bi_sum_expenses", money(gastosTotal), `${gastos.length}`],
              ["fin_bi_sum_refunds", money(reembolsosTotal), `${reembolsos.length}`],
              ["fin_bi_sum_net", money(gastosTotal - reembolsosTotal), ""],
              ["fin_bi_sum_excluded", `${excluidos.length}`, ""],
              ["fin_bi_sum_dups", `${preview.duplicate_count}`, ""],
              ["fin_bi_sum_rows", `${preview.total_rows}`, ""],
            ].map(([k, v, sub]) => (
              <div key={k} data-testid={`fin-bi-${k.replace("fin_bi_sum_", "card-")}`} className="border border-[var(--border)] bg-white p-3">
                <div className="text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t(k)}</div>
                <div className="font-display text-base font-black mt-1">{v}</div>
                {sub && <div className="text-[10px] font-mono-tech text-[var(--text-secondary)]">{sub} mov.</div>}
              </div>
            ))}
          </div>

          <div className="border border-[var(--border)] bg-white overflow-x-auto max-h-[480px] overflow-y-auto">
            <table data-testid="fin-bi-table" className="w-full text-xs min-w-[860px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-black text-left">
                  {["fin_bi_col_date", "fin_bi_col_desc", "fin_bi_col_amount", "fin_bi_col_type", "fin_bi_col_category", "fin_bi_col_dup"].map((c) => (
                    <th key={c} className="px-2 py-2 text-[10px] font-mono-tech uppercase text-[var(--text-secondary)]">{t(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.fingerprint} data-testid={`fin-bi-row-${i}`}
                    className={`border-b border-[var(--border)] ${r.duplicate ? "opacity-45 bg-[var(--surface-alt)]" : ""}`}>
                    <td className="px-2 py-1.5 font-mono-tech whitespace-nowrap">{r.date}</td>
                    <td className="px-2 py-1.5 max-w-[320px] truncate" title={r.description}>{r.description}</td>
                    <td className={`px-2 py-1.5 font-mono-tech text-right whitespace-nowrap ${r.amount < 0 ? "text-[#C5221F]" : "text-[#0F7B3E]"}`}>{money(r.amount)}</td>
                    <td className="px-2 py-1.5">
                      <select value={r.movement_type} disabled={r.duplicate}
                        data-testid={`fin-bi-type-${i}`}
                        onChange={(e) => setRow(i, { movement_type: e.target.value, category: needsCategory(e.target.value) ? (r.category || "otros") : null })}
                        className="border border-[var(--border)] bg-white px-1 py-0.5 text-xs w-full">
                        {TYPES.map((ty) => <option key={ty} value={ty}>{t(`fin_bi_t_${ty}`)}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <select value={r.category || ""} disabled={r.duplicate || !needsCategory(r.movement_type)}
                        data-testid={`fin-bi-cat-${i}`}
                        onChange={(e) => {
                          if (e.target.value === "__new__") { setNewCat({ rowIndex: i }); setNewCatName(""); setCatError(""); return; }
                          setRow(i, { category: e.target.value });
                        }}
                        className="border border-[var(--border)] bg-white px-1 py-0.5 text-xs w-full">
                        {!needsCategory(r.movement_type) && <option value="">—</option>}
                        {CATEGORIES.map((c) => <option key={c} value={c}>{t(`fin_bi_c_${c}`)}</option>)}
                        {activeCustom.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                        <option value="__new__">{t("fin_bi_new_cat")}</option>
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      {r.duplicate && <span data-testid={`fin-bi-dup-${i}`} className="text-[10px] font-mono-tech font-bold text-[#C5221F] uppercase">{t("fin_bi_dup")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button onClick={onConfirm} disabled={busy || selectable.length === 0}
            data-testid="fin-bi-confirm-btn"
            className="border border-black bg-black text-white px-6 py-2 text-sm font-mono-tech hover:bg-white hover:text-black transition-colors disabled:opacity-40">
            {busy ? t("fin_bi_loading") : `${t("fin_bi_confirm")} (${selectable.length})`}
          </button>
        </>
      )}
    </div>
  );
};
